import { useState } from "react"
import { Navigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import styled from "styled-components"

import { useGetIntelligenceReportDetailQuery } from "store/api/contractorsApi"
import { useAppSelector } from "store/hooks"
import { userLogoSelector } from "store/tempDataSlise"
import SubscriptionPlan from "types/enums/SubscriptionPlan"

import { triggerFileDownload } from "utils/triggerFileDownload"
import { useUserProfile } from "hooks/useUserProfile"

import { IntelligenceReportHtmlDocument } from "../htmlReport/IntelligenceReportHtmlDocument"
import { ReportPrintStyles } from "../htmlReport/shared"
import { EmptyState } from "./EmptyState"
import { Toolbar } from "./Toolbar"

const Wrapper = styled.div`
  min-height: 100vh;
  background: #f1f5f9;
  padding-bottom: 24px;

  @media (max-width: 768px) {
    padding-bottom: 12px;
  }

  @media print {
    background: #ffffff;
    padding: 0;
    min-height: 0;
  }
`

const Body = styled.div`
  padding: 24px 0 0;

  @media (max-width: 768px) {
    padding: 12px 0 0;
  }

  @media print {
    padding: 0;
  }
`

const StateMessage = styled.div`
  text-align: center;
  padding: 80px 24px;
  color: #475569;
  font-family: Inter, sans-serif;
  font-size: 20px;
  font-weight: 500;
  line-height: 28px;
`

export function FullIntelligenceReport() {
  const { reportId } = useParams<{ reportId: string }>()
  const userLogo = useAppSelector(userLogoSelector)
  const [isDownloading, setIsDownloading] = useState(false)
  const { userProfile } = useUserProfile()
  const hasEPlusPlan = userProfile?.contractor?.subscription_plan === SubscriptionPlan.E_PLUS

  if (userProfile && !hasEPlusPlan) {
    return <Navigate to="/intelligence-reports" replace />
  }

  const reportIdNum = reportId ? Number(reportId) : Number.NaN
  const isValidId = Number.isFinite(reportIdNum)

  const { data, isFetching, isError } = useGetIntelligenceReportDetailQuery(reportIdNum, {
    skip: !isValidId,
  })

  const detail = data?.data

  const handleDownloadPdf = async () => {
    if (!detail || isDownloading) return
    setIsDownloading(true)
    try {
      const { generateReportPdf } = await import("../pdf/generateReportPdf")
      const blob = await generateReportPdf(detail, userLogo)
      triggerFileDownload(blob, `intelligence-report-${detail.device_sn || reportIdNum}.pdf`)
    } catch (err) {
      console.error("PDF generation failed:", err)
      toast.error("PDF download failed. Please try again later.")
    } finally {
      setIsDownloading(false)
    }
  }

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      toast.success("Link copied to clipboard")
    } catch {
      toast.error("Failed to copy link")
    }
  }

  const renderBody = () => {
    if (!isValidId) return <EmptyState title="Invalid report ID" />
    if (detail) return <IntelligenceReportHtmlDocument report={detail} logoUrl={userLogo} />
    if (isFetching) return <StateMessage>Loading report...</StateMessage>
    if (isError) return <EmptyState title="Failed to load report" description="Please try again later." />
    return <EmptyState title="No report found" />
  }

  return (
    <Wrapper>
      <ReportPrintStyles />
      <Toolbar
        title="Intelligence Report"
        subtitle={detail?.device_sn ? `SN: ${detail.device_sn}` : undefined}
        showActions={Boolean(detail)}
        onDownloadPdf={handleDownloadPdf}
        onCopyLink={handleCopyLink}
        isDownloading={isDownloading}
        isDownloadDisabled={!detail || isDownloading}
      />
      <Body>{renderBody()}</Body>
    </Wrapper>
  )
}
